// Sprint PKG-VIZ A1 (#792) — <PKGCanvas />, the shared React Flow wrapper
// six downstream stories (A2, A3, B2, C2, D1, D2) build against.
//
// See Sprint-Artefacts/Sprint-A1-impl-prompt.md for the full adjudicated
// contract (§4) and the V1-V4 implementation resolutions this file
// applies directly:
//   V1 — fallback-type resolution lives here, off consumers.
//   V2 — layout re-runs when the [layoutHint, node-id-set, edge-id-set]
//        signature changes, not on every render and not only on mount.
//   V3 — fallback predicate derives from the node-type registry.
//   V4 — AI-inferred edges get a dash-only style override.

import { useCallback, useEffect, useMemo, useRef, type CSSProperties } from 'react';
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  BackgroundVariant,
  useNodesState,
  useEdgesState,
  type Node,
  type Edge,
  type NodeMouseHandler,
  type EdgeMouseHandler,
  type Connection,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import type { PKGCanvasProps, PKGNode, PKGEdge } from '@blueprint/shared';
import { nodeTypes, resolveNodeType, type PKGNodeComponentProps } from './nodes/index.ts';
import { edgeStyleFor, buildEdgeId } from './edges/edgeStyle.ts';
import { PKGEdgeWithActions, type PKGEdgeWithActionsData } from './edges/PKGEdgeWithActions.tsx';
import { resolveEdgeAddSignal } from './edges/edgeAdd.ts';
import { computeDagreLayout } from './layouts/dagre.ts';
import { computeRadialLayout } from './layouts/radial.ts';
import { computeForceLayout } from './layouts/force.ts';
import type { PKGLayoutPositions } from './layouts/types.ts';
import { colors } from '../../config/design-tokens.ts';

// React Flow's Node<NodeData> generic requires NodeData to extend
// Record<string, unknown> (an explicit index signature). PKGNode is a
// plain interface on purpose — it's the @blueprint/shared contract and
// shouldn't carry an index signature just to satisfy this apps/web-local
// generic. Intersecting locally synthesizes the index signature without
// touching the shared type.
type PKGFlowNodeData = PKGNode & Record<string, unknown>;
type PKGFlowNode = Node<PKGFlowNodeData>;

// R2 fix (F3): window within which a second click cancels the first's
// deferred onNodeClick, treating the pair as the lead-in to a double-
// click instead. Comfortably above how fast a real double-click's two
// native `click` events land (typically well under 100ms apart).
const NODE_CLICK_DEBOUNCE_MS = 250;

// Founder review follow-up (#1760): React Flow's Controls/MiniMap ship
// stock light-mode styling and nothing in this repo themed them — left
// alone they clash with the Graphite Violet dark canvas in every
// downstream consumer this contract serves. These are React Flow's own
// documented theming custom properties, scoped under `.react-flow`
// (which the <ReactFlow style={...}> prop targets directly) — see
// https://reactflow.dev/learn/customization/theming. Values are drawn
// from the existing design-tokens.ts surface/text palette, not new colors.
const REACT_FLOW_THEME_VARS = {
  '--xy-controls-button-background-color-default': colors.surface.card,
  '--xy-controls-button-background-color-hover-default': colors.surface.cardHover,
  '--xy-controls-button-border-color-default': colors.surface.border,
  '--xy-controls-button-color-default': colors.text.secondary,
  '--xy-controls-button-color-hover-default': colors.text.primary,
  '--xy-minimap-background-color-default': colors.surface.panel,
} as CSSProperties;

// MiniMap's viewport mask isn't covered by a CSS custom property (only
// direct props) — same violet-at-low-alpha pattern shadows.glow already
// uses elsewhere in design-tokens.ts, not a new invented color.
const MINIMAP_MASK_COLOR = 'rgba(124, 58, 237, 0.15)';

function computeLayoutPositions(
  layoutHint: PKGCanvasProps['layoutHint'],
  nodes: PKGNode[],
  edges: PKGEdge[],
  centerNodeId: string | undefined,
): PKGLayoutPositions {
  switch (layoutHint) {
    case 'radial':
      return computeRadialLayout(nodes, edges, centerNodeId);
    case 'force':
      return computeForceLayout(nodes, edges);
    case 'dagre':
    default:
      return computeDagreLayout(nodes, edges, 'LR');
  }
}

function toFlowNodes(nodes: PKGNode[], positions: PKGLayoutPositions): PKGFlowNode[] {
  return nodes.map(node => ({
    id: node.id,
    type: resolveNodeType(node.entityType),
    position: positions.get(node.id) ?? { x: 0, y: 0 },
    // Runtime shape is identical to PKGNode; the cast only satisfies the
    // locally-synthesized index signature (see PKGFlowNodeData above).
    data: node as PKGFlowNodeData,
  }));
}

/** Shared by toFlowEdges and the content-only-refresh merge path (F4) so
 * the two never drift on how an edge's visual style is derived. */
function edgeVisualProps(edge: PKGEdge): Pick<Edge, 'style' | 'markerEnd'> {
  const visual = edgeStyleFor(edge.edgeType, edge.createdByAi);
  return {
    style: { stroke: visual.stroke, strokeWidth: visual.strokeWidth, strokeDasharray: visual.strokeDasharray },
    markerEnd: visual.markerEnd,
  };
}

// R2 fix (F9): PKGEdge.id was declared as caller-supplied but silently
// discarded — the React-Flow-facing id was always buildEdgeId's output,
// so a caller's own edge.id never round-tripped anywhere, including
// back out through onEdgeClick. Normalizing id to buildEdgeId's output
// wherever a PKGEdge is stored/exposed removes that discrepancy: the id
// IS the canonical one, everywhere, always.
function normalizeEdgeId(edge: PKGEdge): PKGEdge {
  return { ...edge, id: buildEdgeId(edge.source, edge.target, edge.edgeType) };
}

// D1 (#801) — the extra bits toFlowEdges needs to know per-edge to decide
// whether it renders as the pill-bearing custom type. Kept as a distinct
// param object (not folded into PKGEdge) since these are UI-wiring
// concerns, not domain data.
interface PKGEdgeCallbacks {
  onEdgeConfirm?: PKGCanvasProps['onEdgeConfirm'];
  onEdgeReject?: PKGCanvasProps['onEdgeReject'];
}

/** Shared by toFlowEdges and the content-only-refresh merge path so the
 * two never drift on which edges get confirm/reject pills (same
 * reasoning as edgeVisualProps above). Always returns BOTH keys
 * explicitly (never omits them) so a merge via `{...flowEdge, ...this}`
 * correctly clears a previously-set type/data if interactionLevel
 * changes back away from 'editable' — an empty-object return here would
 * leave a stale pill-bearing edge stuck in that state. */
function edgeInteractionProps(
  edge: PKGEdge,
  interactionLevel: PKGCanvasProps['interactionLevel'],
  callbacks: PKGEdgeCallbacks,
): Pick<Edge, 'type' | 'data'> {
  const showActions = interactionLevel === 'editable' && edge.createdByAi;
  if (!showActions) return { type: undefined, data: undefined };
  const data: PKGEdgeWithActionsData = {
    domainEdge: edge,
    onConfirm: callbacks.onEdgeConfirm,
    onReject: callbacks.onEdgeReject,
  };
  return { type: 'pkgEdgeWithActions', data };
}

function toFlowEdges(
  edges: PKGEdge[],
  interactionLevel: PKGCanvasProps['interactionLevel'],
  callbacks: PKGEdgeCallbacks,
): Edge[] {
  return edges.map(edge => ({
    id: buildEdgeId(edge.source, edge.target, edge.edgeType),
    source: edge.source,
    target: edge.target,
    ...edgeVisualProps(edge),
    ...edgeInteractionProps(edge, interactionLevel, callbacks),
  }));
}

const edgeTypes = { pkgEdgeWithActions: PKGEdgeWithActions };

/** Sorted id-set signature — see V2. Content-agnostic on purpose: a
 * node's summary/badge changing shouldn't trigger a relayout, only the
 * graph's structure (which nodes/edges exist), the chosen layout, or
 * (R2 F8) which node a radial layout is centered on. */
function structuralSignature(
  layoutHint: string,
  nodes: PKGNode[],
  edges: PKGEdge[],
  centerNodeId: string | undefined,
): string {
  const nodeIds = nodes.map(n => n.id).sort().join(',');
  const edgeIds = edges.map(e => buildEdgeId(e.source, e.target, e.edgeType)).sort().join(',');
  return `${layoutHint}::${centerNodeId ?? ''}::${nodeIds}::${edgeIds}`;
}

export function PKGCanvas({
  nodes,
  edges,
  layoutHint,
  interactionLevel,
  centerNodeId,
  onNodeClick,
  onEdgeClick,
  onNodeExpand,
  onEdgeConfirm,
  onEdgeReject,
  onEdgeAdd,
}: PKGCanvasProps) {
  const initialPositions = useMemo(
    () => computeLayoutPositions(layoutHint, nodes, edges, centerNodeId),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally computed once for initial seed; the effect below owns steady-state relayout.
    [],
  );

  const edgeCallbacks: PKGEdgeCallbacks = { onEdgeConfirm, onEdgeReject };

  const [flowNodes, setFlowNodes, onNodesChange] = useNodesState(toFlowNodes(nodes, initialPositions));
  const [flowEdges, setFlowEdges, onEdgesChange] = useEdgesState(
    toFlowEdges(edges, interactionLevel, edgeCallbacks),
  );

  const prevSignatureRef = useRef<string | null>(null);

  useEffect(() => {
    const signature = structuralSignature(layoutHint, nodes, edges, centerNodeId);
    if (signature === prevSignatureRef.current) {
      // Structurally unchanged (parent re-render, or only node/edge
      // payload fields like `summary`/`badge` changed) — refresh each
      // flow node's `data` so content updates render, but leave React
      // Flow's internal `position` (and any other owned state, e.g. a
      // future editable-mode drag) exactly as it is. See V2.
      const latestById = new Map(nodes.map(n => [n.id, n]));
      setFlowNodes(current =>
        current.map(flowNode => {
          const latest = latestById.get(flowNode.id);
          return latest ? { ...flowNode, data: latest as PKGFlowNodeData } : flowNode;
        }),
      );
      // R2 fix (F4): merge style/data updates without wiping React-
      // Flow-owned edge state (e.g. `selected`) — previously this branch
      // preserved that guarantee for nodes only and did a blanket
      // rebuild for edges, silently un-selecting any selected edge on
      // every content-only refresh.
      const latestEdgesById = new Map(
        edges.map(e => [buildEdgeId(e.source, e.target, e.edgeType), e]),
      );
      setFlowEdges(current =>
        current.map(flowEdge => {
          const latest = latestEdgesById.get(flowEdge.id);
          return latest
            ? {
                ...flowEdge,
                ...edgeVisualProps(latest),
                ...edgeInteractionProps(latest, interactionLevel, { onEdgeConfirm, onEdgeReject }),
              }
            : flowEdge;
        }),
      );
      return;
    }
    prevSignatureRef.current = signature;
    const positions = computeLayoutPositions(layoutHint, nodes, edges, centerNodeId);
    setFlowNodes(toFlowNodes(nodes, positions));
    setFlowEdges(toFlowEdges(edges, interactionLevel, { onEdgeConfirm, onEdgeReject }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- gated by the structural signature computed above, not raw nodes/edges identity.
    // D1 (#801): interactionLevel/onEdgeConfirm/onEdgeReject ARE real deps
    // now (unlike onNodeClick/onEdgeClick below, which are read live in
    // handlers rather than baked into flowEdges' `type`/`data`) — without
    // them here, toggling interactionLevel on an already-mounted canvas
    // with no other structural change would never re-run this effect, and
    // pills would never appear/disappear. Firing this effect more often
    // when a consumer doesn't memoize the callbacks is a cheap content-
    // refresh pass (§ V2), not a relayout — the structural-signature gate
    // above is unaffected by this addition.
  }, [layoutHint, nodes, edges, centerNodeId, interactionLevel, onEdgeConfirm, onEdgeReject]);

  // readonly = pure view, no interaction callbacks fire. interactive and
  // editable both fire callbacks in A1 — editable's drag/reconnect
  // wiring (the actual differentiator) is D1's territory, not built here.
  const interactive = interactionLevel !== 'readonly';

  const nodeById = useMemo(() => new Map(nodes.map(n => [n.id, n])), [nodes]);
  const edgeByFlowId = useMemo(() => {
    const map = new Map<string, PKGEdge>();
    for (const edge of edges) {
      const normalized = normalizeEdgeId(edge);
      map.set(normalized.id, normalized);
    }
    return map;
  }, [edges]);

  // R2 fix (F3): a real physical double-click fires native `click` twice
  // before `dblclick` once — without discrimination, onNodeClick fires
  // (twice) right alongside onNodeExpand on every double-click,
  // contradicting the "exclusive signal" guarantee documented on
  // onNodeExpand in packages/shared/src/pkg-canvas-types.ts. Deferring
  // the single-click callback lets a following double-click cancel it.
  const pendingClickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (pendingClickTimerRef.current) clearTimeout(pendingClickTimerRef.current);
    };
  }, []);

  const handleNodeClick: NodeMouseHandler<PKGFlowNode> = useCallback(
    (_event, node) => {
      if (!interactive || !onNodeClick) return;
      const domainNode = nodeById.get(node.id);
      if (!domainNode) return;
      if (pendingClickTimerRef.current) clearTimeout(pendingClickTimerRef.current);
      pendingClickTimerRef.current = setTimeout(() => {
        pendingClickTimerRef.current = null;
        onNodeClick(domainNode);
      }, NODE_CLICK_DEBOUNCE_MS);
    },
    [interactive, onNodeClick, nodeById],
  );

  const handleNodeDoubleClick: NodeMouseHandler<PKGFlowNode> = useCallback(
    (_event, node) => {
      // Cancel whatever single-click callback the preceding native
      // click(s) scheduled — a real double-click must fire onNodeExpand
      // alone, never alongside onNodeClick.
      if (pendingClickTimerRef.current) {
        clearTimeout(pendingClickTimerRef.current);
        pendingClickTimerRef.current = null;
      }
      if (!interactive || !onNodeExpand) return;
      const domainNode = nodeById.get(node.id);
      if (domainNode) onNodeExpand(domainNode);
    },
    [interactive, onNodeExpand, nodeById],
  );

  const handleEdgeClick: EdgeMouseHandler<Edge> = useCallback(
    (_event, edge) => {
      if (!interactive || !onEdgeClick) return;
      const domainEdge = edgeByFlowId.get(edge.id);
      if (domainEdge) onEdgeClick(domainEdge);
    },
    [interactive, onEdgeClick, edgeByFlowId],
  );

  // D2 (#802) — fired on a completed drag-to-connect gesture. Signal-only,
  // same shape as onEdgeConfirm/onEdgeReject/onEdgeExpand: PKGCanvas does
  // not call the create-edge endpoint itself, does not validate the edge-
  // type matrix (server-side, edge-manual-add.ts), and does not
  // optimistically render the new edge — the caller owns the edge-type-
  // picker UI and re-fetches/re-renders on its own success path. Gating
  // logic lives in edgeAdd.ts's resolveEdgeAddSignal (extracted for
  // direct unit-testing, see PKGCanvas.edgeAdd.test.tsx) — this callback
  // is a thin wrapper.
  const handleConnect = useCallback(
    (connection: Connection) => {
      const signal = resolveEdgeAddSignal(interactionLevel, onEdgeAdd, connection);
      if (signal) onEdgeAdd!(signal);
    },
    [interactionLevel, onEdgeAdd],
  );

  return (
    <div style={{ width: '100%', height: '100%' }} data-testid="pkg-canvas">
      <ReactFlow<PKGFlowNode>
        nodes={flowNodes}
        edges={flowEdges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        // D1 (#801): the affordance A1 stubbed. Pure visual repositioning,
        // no data mutation, no persistence — safe to enable purely on
        // interactionLevel.
        nodesDraggable={interactionLevel === 'editable'}
        // D2 (#802): the affordance D1 deliberately left for this story
        // ("drawing NEW edges by dragging between node handles is manual
        // edge addition, explicitly D2's territory" — D1's own comment,
        // now fulfilled here). Enabling connection handles is the ONLY
        // change from D1's nodesConnectable={false}; onConnect itself
        // does no writing (see handleConnect above).
        nodesConnectable={interactionLevel === 'editable'}
        elementsSelectable={interactive}
        onNodeClick={handleNodeClick}
        onNodeDoubleClick={handleNodeDoubleClick}
        onEdgeClick={handleEdgeClick}
        onConnect={handleConnect}
        fitView
        // Double-click is repurposed as the onNodeExpand signal (adjudication
        // Q7) — React Flow's default "double-click empty space to zoom"
        // would compete with that semantics, and with nodesDraggable=false
        // a node lacks the internal 'nopan' class, so the pane's built-in
        // zoom-on-double-click handler claims the event before it reaches
        // the node at all. Disabling it here is correct, not a workaround.
        //
        // D1 (#801) re-verification (pkg-canvas-types.ts R2/F10 flagged
        // this explicitly for whichever story made nodesDraggable
        // conditional): zoomOnDoubleClick stays FALSE unconditionally,
        // in both draggable and non-draggable states — so the specific
        // hazard F10 warned about (the pane's built-in zoom-on-dblclick
        // handler competing for the event once 'nopan' is restored by
        // nodesDraggable=true) is neutralized the same way regardless of
        // interactionLevel: this prop was never conditioned on
        // nodesDraggable to begin with. See PKGCanvas.editable.test.tsx
        // for the regression test proving onNodeExpand still fires alone
        // (no double-fire with onNodeClick) once dragging is enabled.
        //
        // D2 (#802) re-verification (recon flagged this explicitly — not
        // assumed safe by analogy to D1's nodesDraggable case):
        // nodesConnectable ALSO restores a node's internal 'nopan' class
        // the same way nodesDraggable does. Since zoomOnDoubleClick is
        // hardcoded false above (not conditioned on either prop), the
        // same neutralization holds regardless of which prop restored
        // 'nopan' — but the drag-to-connect gesture itself is a distinct
        // interaction, initiated from a node's connection handle, not
        // its body, so it doesn't compete with click/dblclick the way
        // dragging the node itself could. See
        // PKGCanvas.editable.test.tsx's onConnect coverage for the
        // regression proof that onEdgeAdd fires without onNodeClick/
        // onNodeExpand also firing for the same gesture.
        zoomOnDoubleClick={false}
        style={REACT_FLOW_THEME_VARS}
      >
        <MiniMap nodeColor={colors.primary.DEFAULT} maskColor={MINIMAP_MASK_COLOR} />
        <Controls />
        <Background variant={BackgroundVariant.Dots} gap={16} color={colors.surface.border} />
      </ReactFlow>
    </div>
  );
}

export type { PKGNodeComponentProps };
